import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { hitSlopS, rem } from 'design-system/values';
import IconTooltip from 'design-system/icons/IconTooltip';
import AmountSelectors from 'design-system/AmountSelectors';
import MoneyInput from 'design-system/MoneyInput';
import { AppView, HelpNavigationButton, IconArrow, Indicator } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import { assertUnreachable } from 'utils';
import useHeaderHeight from 'hooks/useHeaderHeight';
import usePasscodeModal from 'hooks/usePasscodeModal';
import { selectFeatureFlag } from 'reducers/selectors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useHelpActionSheet from 'hooks/useHelpActionSheet';
import useFetch, { muteErrorAndResult } from 'hooks/useFetch';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { updateRecurringBuy, getTradingMarketsInfo, getPatternDayTradingInfo, } from '../actions';
import { formatAmount, getClosestDecimalCeilBias } from '../utils';
import useAccount from '../hooks/useAccount';
import BuyLimitOrderModal from '../components/BuyLimitOrderModal';
import { selectTradingAccount, selectIsTradingAccountAlpaca } from '../reducers/selectors';
import { MIN_BUY_AMOUNT, SHARE_QUANTITY_DECIMALS } from '../constants';
import { isWeb } from '../../../constants';
import LimitBuyAmountPreview from '../components/LimitBuyAmountPreview';
import NumberOfSharesPreview from '../components/NumberOfSharesPreview';
import useLimitStopOrderConfirm from '../hooks/useLimitStopOrderConfirm';
import { SelectOrderTypeModal } from '../components/SelectOrderTypeModal';
import { useOrderPreview, useOrderConfirm, useStockMeta } from '../hooks';
import QuantityInput from '../components/QuantityInput';
import { amplitudeSource, useAmplitudeOnMountEvent } from '../utils/analytics';
import { OrderType } from '../types';
import { useCurrentPrice } from '../hooks/selectors';
import BuyScreenDepositButton from '../components/BuyScreenDepositButton';
const strings = {
    available: 'Available ',
    limitBuyButton: 'Shares',
    instantOrderButton: 'Pounds',
    instantOrderButtonOld: 'Market order',
    updateRecurringAmount: 'Confirm',
    reviewOrderButton: 'Review order',
    passcodeTitle: 'Enter Your Pin Code',
    recurringBuyButton: 'Recurring order',
};
const DEFAULT_INITIAL_AMOUNT = 0;
const DEFAULT_INITIAL_QUANTITY = 0;
const EXTRA_BUTTON_SPACING = rem(16 * 2 + 8);
const AMOUNT_SELECTORS = [50, 100, 200];
const BuyCustomAmountScreenComponent = ({ route, symbol, name, navigation, navigation: { navigateDeprecated, pop } }) => {
    const symbolOrName = symbol || name;
    const { assetId } = route.params;
    const moneyInputRef = useRef(null);
    const quantityInputRef = useRef(null);
    const buyInShares = useAppSelector((store) => selectFeatureFlag(store, 'buy_in_shares').value);
    const currentPrice = useCurrentPrice(assetId);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const accountType = tradingAccount?.account.accountType || 'GIA';
    const tradingAccountId = tradingAccount?.account?.id;
    const isAlpacaAccount = !!tradingAccount?.isAlpaca;
    const minimumTransferUnit = useAppSelector((store) => store.invest.caches[assetId]?.minimumTransferUnit);
    const assetType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const recurring = route.params?.recurring;
    const recurringBuy = route.params?.recurringBuy;
    const [exceededMax, setExceededMax] = useState(false);
    const [orderType, setOrderType] = useState(recurring ? OrderType.RECURRING : OrderType.MARKET);
    useAmplitudeOnMountEvent(undefined, 'Order Screen', { type: 'buy', symbol: symbolOrName });
    const colors = useColors();
    const { width } = useAppFrameDimensions(isWeb);
    useEffect(() => {
        if (buyInShares) {
            dispatch(getTradingMarketsInfo());
        }
    }, [buyInShares]);
    const [, , requesting, fetch] = useFetch(muteErrorAndResult);
    const account = useAccount({ refresh: true, skipGetPositions: true });
    const [totalAmount, setTotalAmount] = useState(DEFAULT_INITIAL_AMOUNT);
    const quantityRef = useRef(DEFAULT_INITIAL_QUANTITY);
    const setQuantityRef = useRef(undefined);
    const [fullOrderPreview, getOrderPreview, loading] = useOrderPreview(assetId, tradingAccountId, {
        operation: 'buy',
        updateOnMount: true,
        initialAmount: DEFAULT_INITIAL_AMOUNT,
    });
    const orderPreview = fullOrderPreview?.order;
    // orderPreview taking precedence over currentPrice
    const latestPriceAmount = orderPreview?.estimated.price?.value || currentPrice?.value;
    const min = 
    // eslint-disable-next-line no-nested-ternary
    assetType === 'ETF'
        ? 0.0001
        : minimumTransferUnit && latestPriceAmount
            ? getClosestDecimalCeilBias(minimumTransferUnit * 1.1 * latestPriceAmount)
            : undefined;
    const max = useMemo(() => {
        if (orderType === OrderType.RECURRING || recurringBuy) {
            return undefined;
        }
        return account?.availableToInvest?.value;
    }, [account?.availableToInvest?.value, orderType, recurringBuy]);
    const hasMinError = !!(min && totalAmount && totalAmount < min);
    const hasMaxError = !!(max && totalAmount && totalAmount > max);
    const shouldFetchOrderPreviewOnAvailableChangeRef = useRef(false);
    const [confirm, execute] = useOrderConfirm(symbol, name, assetId, tradingAccountId, isAlpacaAccount, accountType, 'buy');
    const [onConfirmLimitBuy] = useLimitStopOrderConfirm(symbolOrName, tradingAccountId, assetId, 'buy');
    const [getPin] = usePasscodeModal();
    const onAmountChange = useCallback((value) => {
        setTotalAmount(value);
        if (max && value > max) {
            shouldFetchOrderPreviewOnAvailableChangeRef.current = true;
            getOrderPreview(0);
        }
        else {
            shouldFetchOrderPreviewOnAvailableChangeRef.current = false;
            getOrderPreview(value);
        }
        setExceededMax(false);
    }, [getOrderPreview, max]);
    useEffect(() => {
        if (!hasMaxError && shouldFetchOrderPreviewOnAvailableChangeRef.current) {
            getOrderPreview(totalAmount);
        }
    }, [hasMaxError, getOrderPreview, totalAmount]);
    const onQuantityChange = useCallback((value) => {
        const quantity = value;
        quantityRef.current = quantity;
        const currentPrice = store.getState().invest.currentPriceCaches[assetId];
        if (currentPrice) {
            getOrderPreview(quantity, {
                orderType: OrderType.LIMIT,
                limitPrice: currentPrice.value * 1.05,
            }, quantityInputRef);
        }
    }, [assetId, getOrderPreview]);
    const onSuccess = useCallback((order, marketOpeningInfo) => {
        pop(isAlpacaAccount ? 1 : 2);
        navigateDeprecated('InvestOrder', { order, marketOpeningInfo });
        if (isAlpacaAccount) {
            dispatch(getPatternDayTradingInfo());
        }
    }, [isAlpacaAccount]);
    const onConfirm = useCallback(async (orderPreview, pin) => {
        Modal.hide(); // hide order confirmation modal
        await execute(orderPreview, onSuccess, pin);
    }, [execute, onSuccess]);
    const onTapConfirm = useCallback(async (latestOrderPreview, forcePinAuthentication) => {
        // orderPreview will always be defined here, since the button that calls this callback is disabled when orderPreview is not yet defined
        if (latestOrderPreview) {
            const { operation, estimated: { amount }, } = latestOrderPreview;
            const biometryDescription = `You are buying ${symbol ? `$${symbol}` : name} for ${formatAmount(amount)}`;
            try {
                Amplitude.logEvent('Submitted Request', {
                    source: amplitudeSource,
                    type: operation,
                    symbol: symbolOrName,
                });
                const pin = await getPin({
                    title: strings.passcodeTitle,
                    biometryDescription,
                    forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
                });
                try {
                    await onConfirm(latestOrderPreview, pin);
                }
                catch (e) {
                    // Other errors are already handled in execute
                    if ('message' in e && e.message === 'Refresh token expired') {
                        onTapConfirm(latestOrderPreview, true);
                    }
                }
            }
            catch {
                // user cancelled
            }
        }
    }, [getPin, name, onConfirm, symbol, symbolOrName]);
    const onSubmit = useCallback(() => {
        if (orderType === OrderType.RECURRING) {
            navigateDeprecated('InvestInterval', {
                assetId,
                amount: totalAmount,
            });
            return;
        }
        if (orderType === OrderType.LIMIT) {
            if (orderPreview) {
                onConfirmLimitBuy(orderPreview.estimated.price, quantityRef.current, undefined, orderType, orderPreview.estimated.price, orderPreview.estimated.priceTimestamp, orderPreview.estimated.fxConversion, orderPreview.estimated.amount, orderPreview.estimated.priceConvertedToUserCurrency);
            }
            return;
        }
        const amount = {
            value: totalAmount,
            currency: 'GBP',
        };
        confirm({ operation: 'buy', amount }, fullOrderPreview, onTapConfirm);
    }, [orderType, totalAmount, confirm, fullOrderPreview, onTapConfirm, assetId, orderPreview, onConfirmLimitBuy]);
    const handleChooseBuyType = useCallback(() => {
        SelectOrderTypeModal.show(symbol, isAlpacaAccount, (orderType) => {
            setOrderType(orderType);
            if (orderType === OrderType.MARKET) {
                if (account?.availableToInvest && account.availableToInvest.value < totalAmount) {
                    moneyInputRef.current?.clear();
                }
            }
            else if (orderType === OrderType.RECURRING) {
                setExceededMax(false);
            }
        });
    }, [account.availableToInvest, isAlpacaAccount, symbol, totalAmount]);
    const styles = useStyles(styleSet);
    const handleUpdateRecurringBuy = useCallback(async () => {
        if (recurringBuy) {
            try {
                const response = await fetch(updateRecurringBuy(recurringBuy.id, {
                    amount: totalAmount,
                }));
                if (response?.recurringBuy) {
                    navigation.navigateDeprecated('RecurringBuy', {
                        recurringBuy: response.recurringBuy,
                    });
                }
            }
            catch {
                //
            }
        }
    }, [recurringBuy, totalAmount]);
    const btnText = useMemo(() => {
        switch (orderType) {
            case OrderType.MARKET:
                return buyInShares ? strings.instantOrderButton : strings.instantOrderButtonOld;
            case OrderType.RECURRING:
                return strings.recurringBuyButton;
            case OrderType.LIMIT:
                return strings.limitBuyButton;
            default:
                assertUnreachable(orderType);
                return '';
        }
    }, [buyInShares, orderType]);
    const renderButtons = useMemo(() => {
        if (recurringBuy) {
            return (<View style={styles.buttonContainer}>
          <Button flex brand isFetching={requesting} onPress={handleUpdateRecurringBuy} disabled={!orderPreview || loading} title={strings.updateRecurringAmount}/>
        </View>);
        }
        return (<View style={styles.buttonContainer}>
        {isAlpacaAccount ? (<>
            <Button brandReversed title={btnText} onPress={handleChooseBuyType} rightIcon={<IconArrow direction="down" color={colors.text.textOnBrand}/>} containerStyle={[{ minWidth: (width - EXTRA_BUTTON_SPACING) / 2 }, styles.orderTypeBtn]}/>
            <Spacer w={8}/>
          </>) : (<BuyScreenDepositButton showContent={hasMaxError}/>)}
        <Button flex brand onPress={onSubmit} title={strings.reviewOrderButton} disabled={!orderPreview || loading || hasMinError || hasMaxError}/>
      </View>);
    }, [
        recurringBuy,
        styles.buttonContainer,
        styles.orderTypeBtn,
        isAlpacaAccount,
        btnText,
        handleChooseBuyType,
        colors.text.textOnBrand,
        width,
        hasMaxError,
        onSubmit,
        orderPreview,
        loading,
        hasMinError,
        requesting,
        handleUpdateRecurringBuy,
    ]);
    const numPadHeader = useMemo(() => {
        const amountInfo = (() => {
            if (account) {
                if (exceededMax || hasMaxError) {
                    return (<Text TextThin-14 Red>
              {'Only '}
              <Text NumbersThin-14 centered Red>
                {formatAmount(account.availableToInvest)}
              </Text>
              {' available'}
            </Text>);
                }
                if (hasMinError) {
                    return (<Text TextThin-14 Red>
              {'Minimum order amount '}
              <Text NumbersThin-14 centered Red>
                {formatAmount({ value: min, currency: 'GBP' })}
              </Text>
            </Text>);
                }
                return (<Text NumbersThin-14 centered Secondary>
            <Text TextThin-14 Secondary>
              {strings.available}
            </Text>
            <Text NumbersThin-14 centered Secondary>
              {formatAmount(account.availableToInvest)}
            </Text>
          </Text>);
            }
            return null;
        })();
        return (<View style={styles.numPadHeader}>
        {amountInfo}
        <Spacer h={24}/>
        <View>
          {isAlpacaAccount ? (<AmountSelectors amounts={AMOUNT_SELECTORS} moneyInputRef={moneyInputRef} maxAmount={account?.availableToInvest}/>) : (!!account?.availableToInvest?.value && (<Button small square brandLight title="Invest all" containerStyle={styles.investAllBtn} onPress={() => moneyInputRef.current?.setAmount(account.availableToInvest.value)}/>))}
        </View>
        <Spacer h={12}/>
        <Spacer flex/>
        <NumberOfSharesPreview assetId={assetId} hasFXFee={isAlpacaAccount} latestPrice={orderPreview?.estimated.price} quantity={orderPreview?.estimated.quantity} loading={loading}/>
      </View>);
    }, [
        account,
        assetId,
        exceededMax,
        hasMaxError,
        hasMinError,
        isAlpacaAccount,
        loading,
        min,
        orderPreview?.estimated.price,
        orderPreview?.estimated.quantity,
        styles.investAllBtn,
        styles.numPadHeader,
    ]);
    const showLimitOrderToolTip = useCallback(() => {
        BuyLimitOrderModal.show(symbolOrName, assetId, quantityRef.current);
    }, [assetId, symbolOrName]);
    const numPadHeaderForQuantityInput = useMemo(() => (<View style={styles.numPadHeader}>
        {!!account && (<View style={styles.qtyTextContainer}>
            <Text NumbersThin-14 centered Secondary>
              <Text TextThin-14 Secondary>
                {strings.available}
              </Text>
              <Text NumbersThin-14 centered Secondary>
                {formatAmount(account.availableToInvest)}
              </Text>
              {' • '}
              <Text TextThin-14 Secondary>
                Limit order
              </Text>
            </Text>
            <Spacer w={4}/>
            <View>
              <Spacer h={3}/>
              <TouchableOpacity onPress={showLimitOrderToolTip} hitSlop={hitSlopS}>
                <IconTooltip height={16} width={16} colorKey="secondary"/>
              </TouchableOpacity>
            </View>
          </View>)}
        <Spacer flex/>
        <LimitBuyAmountPreview loading={loading} assetId={assetId} quantityInputRef={quantityInputRef} numberOfShares={orderPreview?.estimated.quantity} fxConversion={orderPreview?.estimated.fxConversion}/>
      </View>), [
        account,
        loading,
        orderPreview?.estimated.fxConversion,
        orderPreview?.estimated.quantity,
        showLimitOrderToolTip,
        assetId,
        styles.numPadHeader,
        styles.qtyTextContainer,
    ]);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
    }), [colors.text.primary]);
    const onExceedMax = useCallback(() => {
        setExceededMax(true);
    }, []);
    const headerHeight = useHeaderHeight();
    const paddingTop = useMemo(() => ({
        paddingTop: headerHeight * 0.6,
    }), [headerHeight]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container, paddingTop);
    return (<AppView type="primary">
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        {orderType !== OrderType.LIMIT ? (<MoneyInput flex ref={moneyInputRef} colors={moneyInputColors} numPadHeader={numPadHeader} onValueChange={onAmountChange} pinPadStyle={styles.pinPadStyle} initialValue={DEFAULT_INITIAL_AMOUNT} max={isAlpacaAccount ? max : undefined} inputFieldStyle={styles.inputFieldStyle} restrictAmountsLessThanOne={MIN_BUY_AMOUNT === 1} onExceedMax={isAlpacaAccount ? onExceedMax : undefined}/>) : (<QuantityInput flex ref={quantityInputRef} colors={moneyInputColors} onExceedMax={onExceedMax} setValueRef={setQuantityRef} onValueChange={onQuantityChange} pinPadStyle={styles.pinPadStyle} initialValue={DEFAULT_INITIAL_QUANTITY} inputFieldStyle={styles.inputFieldStyle} numPadHeader={numPadHeaderForQuantityInput} maximumFractionDigits={SHARE_QUANTITY_DECIMALS}/>)}
        <Spacer h={20}/>
        {renderButtons}
      </View>
    </AppView>);
};
const BuyCustomAmountScreen = ({ route, navigation }) => {
    const { assetId } = route.params;
    const colors = useColors();
    const stockCache = useStockMeta(assetId);
    const showHelpActionSheet = useHelpActionSheet();
    const isTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const symbol = stockCache?.symbol;
    const name = stockCache?.name;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: isTradingAccountAlpaca && symbol ? `Buy $${symbol}` : 'Buy',
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpNavigationButton black onPress={showHelpActionSheet}/>,
        });
    }, [colors.text.primary, isTradingAccountAlpaca, showHelpActionSheet, symbol]);
    if (name) {
        return <BuyCustomAmountScreenComponent name={name} route={route} symbol={symbol} navigation={navigation}/>;
    }
    return (<AppView type="primary">
      <Indicator dark/>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    numPadHeader: {
        flex: 3,
        width: '100%',
        alignItems: 'center',
    },
    qtyTextContainer: {
        flexDirection: 'row',
    },
    pinPadStyle: {
        marginTop: rem(10),
    },
    inputFieldStyle: {
        fontSize: rem(64),
        lineHeight: rem(87.3),
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(16),
    },
    orderTypeBtn: {
        paddingHorizontal: rem(24),
    },
    investAllBtn: {
        paddingHorizontal: rem(24),
    },
}));
export default BuyCustomAmountScreen;
